import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { format } from "date-fns";
// @ts-ignore
import { domainConfigs } from "../../../../apps/web/src/config/domains";


interface Passengers {
  adult: number;
  child: number;
  infant: number;
  cabin: string;
  cabinId: string;
}

interface Seat {
  id: string;
  code: string;
  amount: number;
  desc: string;
  value: string;
}

interface Meal {
  id: string;
  code: string;
  amount: number;
  desc: string;
  value: string;
}

interface Baggage {
  id: string;
  code: string;
  amount: number;
  desc: string;
  value: string;
}

interface SSR {
  seat: Seat[];
  meal: Meal[];
  baggage: Baggage[];
}

interface Passenger {
  title: string;
  firstName: string;
  lastName: string;
  dob: string;
  travelWith: string;
  passport_no: string;
  issue_date: string;
  nationality: string;
  expiry_date: string;
  issue_country: string;
  ssrInfo: SSR[];
}

interface FlightState {
  airlineName: string;
  date: string;
  destination: string;
  destinationCountry: string;
  directFlight: string;
  isRT: boolean;
  origin: string;
  originCountry: string;
  passengers: Passengers;
  providerCode: string;
  rt_date: string;
  travelMode: string;
  travelType: string;
  tripType: string;
  fareType: string[];
  passenger: Passenger[];
  isCityPicker?: boolean;
  origin_city: string;
  destination_city: string;
  origin_airport: string;
  destination_airport: string;
  gst: {
    register_no: string;
    register_company_name: string;
    register_email: string;
    register_address: string;
    register_mobile: string;
  };
  data: any[];
  priceIds: any[];
  fareRules: any[];
  fare_details: any[];
  api_booking_id: any[];
  trip_type: string;
  selectedSSR: any[];
  country_code: string;
  country_code_id: string;
  mobile_no: string;
  email_id: string;
  remarks: any;
  booking_id: string;
  bookingRefId: string;
  bookingStatus: string;
  statusCode: string;
  fareDetails: any[];
  priceDetails: any[];
  ssrInfo: any[];
  cartId: string;
  selectBucket: any[];
  returnBucket: any[];
  initiateReqData: any;
  isLoader: boolean;
}

const today = new Date();

const hostname = typeof window !== 'undefined' ? window.location.hostname : 'default';
const domainConfig = domainConfigs[hostname] || domainConfigs['default'];
const isTf = domainConfig?.corporateName === 'Trailfinder';

const initialState: FlightState | any = {
  airlineName: "",
  date: format(today, "yyyy-MM-dd"),
  rt_date: format(today, "yyyy-MM-dd"),
  destination: isTf ? "MAA" : "BOM",
  destinationCountry: "India",
  directFlight: "N",
  isRT: true,
  origin: isTf ? "BOM" : "MAA",
  originCountry: "India",
  origin_city: isTf ? "Mumbai" : "Chennai",
  destination_city: isTf ? "Chennai" : "Mumbai",
  origin_airport: isTf ? "Chhatrapati Shivaji International Airport" : "Chennai International Airport",
  destination_airport: isTf ? "Chennai International Airport" : "Chhatrapati Shivaji International Airport",
  fareType: ["Regular"],
  passengers: {
    adult: 1,
    child: 0,
    infant: 0,
    cabin: "Economy",
    cabinId: "EC",
  },
  gst: {
    register_no: "",
    register_company_name: "",
    register_email: "",
    register_address: "",
    register_mobile: "",
  },
  providerCode: "TBO",
  travelMode: "DO",
  travelType: "OW",
  tripType: "OW",
  passenger: [],
  data: [],
  priceIds: [],
  fareRules: [],
  fare_details: [],
  api_booking_id: [],
  trip_type: "DO",
  selectedSSR: [],
  country_code: "+91   (India)",
  country_code_id: "+91",
  mobile_no: "",
  email_id: "",
  remarks: null,
  booking_id: "",
  bookingRefId: "",
  bookingStatus: "",
  statusCode: "",
  fareDetails: [],
  priceDetails: [],
  ssrInfo: [],
  cartId: "",
  selectBucket: [],
  returnBucket: [],
  initiateReqData: {},
  isLoader: false,
};

const loadFromsessionStorage = () => {
  if (typeof window === "undefined") {
    return initialState;
  }

  try {
    const hostname = window.location.hostname;
    const domainConfig = domainConfigs[hostname] || domainConfigs['default'];
    const isTf = domainConfig?.corporateName === 'Trailfinder';

    const baseState = {
      ...initialState,
      origin: isTf ? "BOM" : "MAA",
      destination: isTf ? "MAA" : "BOM",
      origin_city: isTf ? "Mumbai" : "Chennai",
      destination_city: isTf ? "Chennai" : "Mumbai",
      origin_airport: isTf ? "Chhatrapati Shivaji International Airport" : "Chennai International Airport",
      destination_airport: isTf ? "Chennai International Airport" : "Chhatrapati Shivaji International Airport",
    };

    const cityData = sessionStorage.getItem("cityData");
    const reqDataStr = sessionStorage.getItem("reqData");
    const reqData = reqDataStr ? JSON.parse(reqDataStr) : {};

    if (cityData) {
      const parsedData = JSON.parse(cityData);
      return {
        ...baseState,
        ...reqData,
        origin: parsedData.origin || baseState.origin,
        destination: parsedData.destination || baseState.destination,
        origin_city: parsedData.origin_city || baseState.origin_city,
        destination_city:
          parsedData.destination_city || baseState.destination_city,
        originCountry: parsedData.originCountry || baseState.originCountry,
        destinationCountry:
          parsedData.destinationCountry || baseState.destinationCountry,
        origin_airport:
          parsedData.origin_airport || baseState.origin_airport,
        destination_airport:
          parsedData.destination_airport || baseState.destination_airport,
      };
    }
    
    // If we have reqData but no cityData, we still want to merge reqData on top of the correct baseState
    return {
      ...baseState,
      ...reqData
    };
  } catch (error) {
    console.error("Failed to load from sessionStorage", error);
    return initialState;
  }
};

const initialStateWithStorage = loadFromsessionStorage();

// Debounced sessionStorage update
let storageTimeout: NodeJS.Timeout | null = null;
const debouncedStorageUpdate = (state: any) => {
  sessionStorage.setItem("reqData", JSON.stringify(state));
};

const flightSlice = createSlice({
  name: "flightSearch",
  initialState: initialStateWithStorage,
  reducers: {
    setSelectBucket: (state, action: PayloadAction<any>) => {
      state.selectBucket = action.payload;
      debouncedStorageUpdate(state);
    },
    setReturnBucket: (state, action: PayloadAction<any>) => {
      state.returnBucket = action.payload;
      debouncedStorageUpdate(state);
    },
    updateField: (
      state,
      action: PayloadAction<{ field: keyof FlightState; value: any }>
    ) => {
      const { field, value } = action.payload;
      if (field in state) {
        state[field] = value;
      } else {
        console.warn(`Field ${field} does not exist in state.`);
      }
      if (state.travelType === "OW") {
        state.rt_date = state.date;
      }
      debouncedStorageUpdate(state);
    },

    updateReqData: (state, action: PayloadAction<FlightState>) => {
      Object.assign(state, action.payload);
      if (state.travelType === "OW") {
        state.rt_date = state.date;
      }
      debouncedStorageUpdate(state);
    },

    updatePriceDetails: (state, action: PayloadAction<any>) => {
      state.priceDetails = action.payload;
      debouncedStorageUpdate(state);
    },

    updatePassengerCount: (state, action: PayloadAction<Passengers>) => {
      state.passengers = action.payload;
      debouncedStorageUpdate(state);
    },

    updatePassenger: (state, action: PayloadAction<any>) => {
      state.passenger = action.payload;
      debouncedStorageUpdate(state);
    },

    updateSelectedSSR: (state, action: PayloadAction<any>) => {
      state.selectedSSR = action.payload;
      debouncedStorageUpdate(state);
    },

    updateReviewData: (state, action: PayloadAction<any>) => {
      Object.assign(state, action.payload);
      if (state.travelType === "OW") {
        state.rt_date = state.date;
      }
      debouncedStorageUpdate(state);
    },

    updateGst: (state, action: PayloadAction<any>) => {
      state.gst = action.payload;
      debouncedStorageUpdate(state);
    },

    updateFareDetails: (state, action: PayloadAction<any>) => {
      state.fareDetails = action.payload;
      debouncedStorageUpdate(state);
    },

    updateInitiateReqData: (state, action: PayloadAction<any>) => {
      state.initiateReqData = action.payload;
      debouncedStorageUpdate(state);
    },

    updateSsrInfo: (state, action: PayloadAction<any>) => {
      state.ssrInfo = action.payload;
      debouncedStorageUpdate(state);
    },

    resetReviewData: (state) => {
      state.data = [];
      state.fareRules = [];
      state.fare_details = [];
      state.priceIds = [];
      state.api_booking_id = [];
      state.bookingStatus = "";
      state.statusCode = "";

      debouncedStorageUpdate(state);
    },

    updateStateData: (state, action: PayloadAction<any>) => {
      Object.assign(state, action.payload);
      if (state.travelType === "OW") {
        state.rt_date = state.date;
      }
      debouncedStorageUpdate(state);
    },

    updateFromLocalState: (state, action: PayloadAction<FlightState>) => {
      state = JSON.parse(sessionStorage.getItem("reqData") || "{}");
      if (state.travelType === "OW") {
        state.rt_date = state.date;
      }
    },

    clearLocalState: (state) => {
      sessionStorage.removeItem("reqData");
      return initialState;
    },

    updateFareType: (state, action: PayloadAction<any>) => {
      Object.assign(state, { fareType: action.payload });
      debouncedStorageUpdate(state);
    },

    updateCartId: (state, action: PayloadAction<any>) => {
      state.cartId = action.payload;
      debouncedStorageUpdate(state);
    },

    isLoader: (state, action: PayloadAction<boolean>) => {
      state.isLoader = action.payload;
      debouncedStorageUpdate(state);
    },

    isCityPicker: (state, action: PayloadAction<boolean>) => {
      state.isCityPicker = action.payload;
      debouncedStorageUpdate(state);
    },
    clearDates: (state) => {
      ((state.date = format(today, "yyyy-MM-dd")),
        (state.rt_date = format(today, "yyyy-MM-dd")));
    },
  },
});

export const {
  updateField,
  updatePassengerCount,
  updatePassenger,
  updateReqData,
  updateFromLocalState,
  updateReviewData,
  clearLocalState,
  updateSelectedSSR,
  updateStateData,
  resetReviewData,
  updateGst,
  updateFareDetails,
  updateFareType,
  updatePriceDetails,
  updateSsrInfo,
  updateCartId,
  setSelectBucket,
  setReturnBucket,
  updateInitiateReqData,
  isLoader,
  isCityPicker,
  clearDates,
} = flightSlice.actions;

export default flightSlice.reducer;
